import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  addStar,
  GqlAddStarInput,
  GqlFactoryTrendViewFragment,
  GqlRemoveStarInput,
  GqlStarrableStateFragment,
  GqlUpdateTrendViewInput,
  removeStar,
  updateTrendView,
} from 'src/services'
import { FETCH_FACTORY_TREND_VIEWS_QUERY } from './trend.get'

export function useUpdateFactoryTrendView(): UseMutationResult<
  GqlFactoryTrendViewFragment,
  unknown,
  GqlUpdateTrendViewInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: GqlUpdateTrendViewInput) => {
      const trendView = await updateTrendView(input)
      if (!trendView) {
        throw new Error('Trend view update failed')
      }
      return trendView
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_FACTORY_TREND_VIEWS_QUERY, data.factoryId],
      })
    },
  })
}

export function useAddStarMutation(
  currentFactory: string,
): UseMutationResult<
  GqlStarrableStateFragment | null | undefined,
  unknown,
  GqlAddStarInput,
  unknown
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: GqlAddStarInput) => addStar(input),
    onMutate: async input => {
      // Optimistically update the query data on trend views page
      queryClient.setQueryData(
        [FETCH_FACTORY_TREND_VIEWS_QUERY, currentFactory],
        (data: GqlFactoryTrendViewFragment[] | undefined) => {
          return data?.map(v => {
            if (v.id === input.starrableId) {
              return { ...v, viewerHasStarred: true }
            }
            return v
          })
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        FETCH_FACTORY_TREND_VIEWS_QUERY,
        currentFactory,
      ])
    },
  })
}

export function useRemoveStarMutation(
  currentFactory: string,
): UseMutationResult<
  GqlStarrableStateFragment | null | undefined,
  unknown,
  GqlRemoveStarInput,
  unknown
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: GqlRemoveStarInput) => removeStar(input),
    onMutate: async input => {
      // Optimistically update the query data on trend views page
      queryClient.setQueryData(
        [FETCH_FACTORY_TREND_VIEWS_QUERY, currentFactory],
        (data: GqlFactoryTrendViewFragment[] | undefined) => {
          return data?.map(v => {
            if (v.id === input.starrableId) {
              return { ...v, viewerHasStarred: false }
            }
            return v
          })
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        FETCH_FACTORY_TREND_VIEWS_QUERY,
        currentFactory,
      ])
    },
  })
}
